import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X,
  CheckCheck,
  RefreshCw,
  Copy,
  Bell,
  ChevronDown,
  ChevronUp,
  Trash2,
  Check,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import {
  useNotifications,
  useMarkAllRead,
  useResolveAllNotifications,
} from '../api/useNotifications';
import { NotificationItem } from './NotificationItem';
import { RecurringGroupCard } from './RecurringActionCard';
import { DuplicateActionCard } from './DuplicateActionCard';
import { useProposedMatchesGrouped, useResolveProposedPayment } from '../../recurring/api/useRecurring';
import type { RecurringType } from '../../recurring/api/useRecurring';
import { useDuplicates, dismissDuplicateGroup } from '../../transactions/api/useDuplicates';
import { mutateData } from '../../../api/client';
import { useQueryClient } from '@tanstack/react-query';

interface ActionCenterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type SectionKey = 'recurring' | 'duplicates' | 'notifications';


export function ActionCenterDrawer({ isOpen, onClose }: ActionCenterDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Notifications
  const { data: notifications = [], isLoading: isLoadingNotifications } = useNotifications({ resolved: false });
  const markAllRead = useMarkAllRead();
  const resolveAll = useResolveAllNotifications();

  // Proposed recurring matches (grouped by entity)
  const { data: proposedData, isLoading: isLoadingProposed } = useProposedMatchesGrouped();
  const resolvePayment = useResolveProposedPayment();

  const proposedGroups = useMemo(
    () => proposedData?.grouped ?? [],
    [proposedData],
  );

  // Duplicate transactions
  const { data: duplicateData, isLoading: isLoadingDuplicates } = useDuplicates();
  const duplicateGroups = duplicateData?.groups ?? [];

  // Local state for duplicate management
  const [resolvedDupGroups, setResolvedDupGroups] = useState<Set<number>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [selectedDupIds, setSelectedDupIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState<Set<SectionKey>>(new Set());

  // Track which payment is being confirmed/rejected
  const [confirmingPaymentId, setConfirmingPaymentId] = useState<string | null>(null);
  const [rejectingPaymentId, setRejectingPaymentId] = useState<string | null>(null);

  // Filter out recurring-specific notifications (they are shown as rich cards instead)
  const otherNotifications = useMemo(() => {
    return notifications.filter(
      (n) =>
        n.actionType !== 'confirm_recurring' &&
        n.actionType !== 'resolve_duplicate',
    );
  }, [notifications]);

  // Section counts
  const recurringCount = proposedGroups.length;
  const duplicateCount = duplicateGroups.filter((_, i) => !resolvedDupGroups.has(i)).length;
  const notificationCount = otherNotifications.length;
  const totalActionCount = recurringCount + duplicateCount + notificationCount;

  const isLoading = isLoadingNotifications || isLoadingProposed || isLoadingDuplicates;

  const handleToggleDupSelect = useCallback((txId: string) => {
    setSelectedDupIds((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) {
        next.delete(txId);
      } else {
        next.add(txId);
      }
      return next;
    });
  }, []);

  const handleToggleDupSelectAll = useCallback(
    (groupIndex: number) => {
      const group = duplicateGroups[groupIndex];
      const txs = group.transactions ?? [];
      const visibleTxIds = txs
        .filter((tx) => !deletedIds.has(tx.id))
        .map((tx) => tx.id);

      setSelectedDupIds((prev) => {
        const allSelected = visibleTxIds.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allSelected) {
          for (const id of visibleTxIds) {
            next.delete(id);
          }
        } else {
          for (const id of visibleTxIds) {
            next.add(id);
          }
        }
        return next;
      });
    },
    [duplicateGroups, deletedIds],
  );

  const dupSelectionSummary = useMemo(() => {
    if (selectedDupIds.size === 0) return { txCount: 0, groupCount: 0 };
    let groupCount = 0;
    for (let i = 0; i < duplicateGroups.length; i++) {
      if (resolvedDupGroups.has(i)) continue;
      const txs = duplicateGroups[i].transactions ?? [];
      if (txs.some((tx) => selectedDupIds.has(tx.id) && !deletedIds.has(tx.id))) {
        groupCount++;
      }
    }
    return { txCount: selectedDupIds.size, groupCount };
  }, [selectedDupIds, duplicateGroups, resolvedDupGroups, deletedIds]);

  if (!isOpen) return null;

  function toggleSection(section: SectionKey) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }

  function handleConfirmPayment(entityId: string, paymentId: string, type?: RecurringType) {
    setConfirmingPaymentId(paymentId);
    resolvePayment.mutate(
      { entityId, paymentId, action: 'confirm', ...(type ? { type } : {}) },
      {
        onSettled: () => setConfirmingPaymentId(null),
      },
    );
  }

  function handleRejectPayment(entityId: string, paymentId: string) {
    setRejectingPaymentId(paymentId);
    resolvePayment.mutate(
      { entityId, paymentId, action: 'reject' },
      {
        onSettled: () => setRejectingPaymentId(null),
      },
    );
  }

  function handleViewRecurringEntity(entityId: string) {
    onClose();
    navigate(`/recurring/${entityId}`, { state: { from: location.pathname } });
  }

  async function handleDeleteDuplicateTransaction(txId: string, groupIndex: number) {
    setDeletingIds((prev) => new Set([...prev, txId]));
    try {
      await mutateData(`/api/transactions/${txId}`, 'DELETE');
      setDeletedIds((prev) => {
        const next = new Set([...prev, txId]);
        const group = duplicateGroups[groupIndex];
        const remaining = group.transactionIds.filter(
          (id) => id !== txId && !next.has(id),
        );
        if (remaining.length <= 1) {
          setResolvedDupGroups((prevResolved) => new Set([...prevResolved, groupIndex]));
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    } catch {
      // Silently fail, user can retry
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(txId);
        return next;
      });
    }
  }

  function handleKeepAllDuplicates(groupIndex: number) {
    const group = duplicateGroups[groupIndex];
    dismissDuplicateGroup(group);
    setResolvedDupGroups((prev) => new Set([...prev, groupIndex]));
  }

  async function handleBulkDeleteDuplicates() {
    if (selectedDupIds.size === 0) return;
    setBulkDeleting(true);

    const idsToDelete = [...selectedDupIds];
    for (const txId of idsToDelete) {
      setDeletingIds((prev) => new Set([...prev, txId]));
      try {
        await mutateData(`/api/transactions/${txId}`, 'DELETE');
        setDeletedIds((prev) => new Set([...prev, txId]));
        setSelectedDupIds((prev) => {
          const next = new Set(prev);
          next.delete(txId);
          return next;
        });
      } catch {
        // Continue with remaining
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(txId);
          return next;
        });
      }
    }

    // Check for resolved groups
    setDeletedIds((currentDeleted) => {
      for (let i = 0; i < duplicateGroups.length; i++) {
        if (resolvedDupGroups.has(i)) continue;
        const group = duplicateGroups[i];
        const remaining = group.transactionIds.filter(
          (id) => !currentDeleted.has(id),
        );
        if (remaining.length <= 1) {
          setResolvedDupGroups((prev) => new Set([...prev, i]));
        }
      }
      return currentDeleted;
    });

    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    setBulkDeleting(false);
  }

  function handleBulkKeepDuplicates() {
    if (selectedDupIds.size === 0) return;
    for (let i = 0; i < duplicateGroups.length; i++) {
      if (resolvedDupGroups.has(i)) continue;
      const group = duplicateGroups[i];
      const txs = group.transactions ?? [];
      if (txs.some((tx) => selectedDupIds.has(tx.id) && !deletedIds.has(tx.id))) {
        dismissDuplicateGroup(group);
        setResolvedDupGroups((prev) => new Set([...prev, i]));
      }
    }
    setSelectedDupIds(new Set());
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />

      {/* Drawer - wider to accommodate rich cards */}
      <div className="fixed right-0 top-0 bottom-0 w-[32rem] max-w-full bg-gray-50 shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Action Center</h2>
            {totalActionCount > 0 && (
              <p className="text-xs text-gray-500 mt-0.5">
                {totalActionCount} pending action{totalActionCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                resolveAll.mutate(undefined as never);
                markAllRead.mutate(undefined as never);
              }}
              disabled={resolveAll.isPending || markAllRead.isPending}
              title="Resolve all notifications"
            >
              <CheckCheck className="h-4 w-4" />
            </Button>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}

          {!isLoading && totalActionCount === 0 && (
            <div className="text-center py-12">
              <CheckCheck className="h-10 w-10 text-green-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm font-medium">All caught up!</p>
              <p className="text-gray-400 text-xs mt-1">No pending actions</p>
            </div>
          )}

          {/* Recurring Matches Section */}
          {!isLoading && recurringCount > 0 && (
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('recurring')}
                className="flex items-center justify-between w-full px-5 py-3 hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-blue-600" />
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Recurring Matches
                  </h3>
                  <span
                    className="text-[10px] text-white font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                    style={{ backgroundColor: '#3b82f6' }}
                  >
                    {recurringCount}
                  </span>
                </div>
                {collapsedSections.has('recurring') ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {!collapsedSections.has('recurring') && (
                <div className="px-4 pb-4 space-y-3">
                  {proposedGroups.map((group) => (
                    <RecurringGroupCard
                      key={group.recurringEntityId}
                      group={group}
                      onConfirm={handleConfirmPayment}
                      onReject={handleRejectPayment}
                      onViewEntity={handleViewRecurringEntity}
                      confirmingPaymentId={confirmingPaymentId}
                      rejectingPaymentId={rejectingPaymentId}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Duplicate Transactions Section */}
          {!isLoading && duplicateCount > 0 && (
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('duplicates')}
                className="flex items-center justify-between w-full px-5 py-3 hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Copy className="h-4 w-4 text-amber-600" />
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Duplicate Transactions
                  </h3>
                  <span
                    className="text-[10px] text-white font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                    style={{ backgroundColor: '#d97706' }}
                  >
                    {duplicateCount}
                  </span>
                </div>
                {collapsedSections.has('duplicates') ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {!collapsedSections.has('duplicates') && (
                <div className="px-4 pb-4 space-y-3">
                  {/* Bulk action bar */}
                  {selectedDupIds.size > 0 && (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2.5">
                      <p className="text-xs font-medium text-blue-800">
                        {dupSelectionSummary.txCount} selected across{' '}
                        {dupSelectionSummary.groupCount} group
                        {dupSelectionSummary.groupCount > 1 ? 's' : ''}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBulkKeepDuplicates}
                          disabled={bulkDeleting}
                          className="text-xs h-7 px-2"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Keep
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBulkDeleteDuplicates}
                          disabled={bulkDeleting}
                          className="text-xs h-7 px-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          {bulkDeleting ? '...' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {duplicateGroups.map((group, groupIndex) => {
                    if (resolvedDupGroups.has(groupIndex)) return null;
                    return (
                      <DuplicateActionCard
                        key={groupIndex}
                        group={group}
                        groupIndex={groupIndex}
                        onDeleteTransaction={(txId) =>
                          handleDeleteDuplicateTransaction(txId, groupIndex)
                        }
                        onKeepAll={() => handleKeepAllDuplicates(groupIndex)}
                        deletingIds={deletingIds}
                        deletedIds={deletedIds}
                        selectedIds={selectedDupIds}
                        onToggleSelect={handleToggleDupSelect}
                        onToggleSelectAll={() => handleToggleDupSelectAll(groupIndex)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Other Notifications Section */}
          {!isLoading && notificationCount > 0 && (
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('notifications')}
                className="flex items-center justify-between w-full px-5 py-3 hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-gray-500" />
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Notifications
                  </h3>
                  <span
                    className="text-[10px] text-white font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                    style={{ backgroundColor: '#6b7280' }}
                  >
                    {notificationCount}
                  </span>
                </div>
                {collapsedSections.has('notifications') ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {!collapsedSections.has('notifications') && (
                <div className="px-4 pb-4 space-y-2">
                  {otherNotifications.map((n) => (
                    <NotificationItem key={n.id} notification={n} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
